export type OrderHistoryStatus =
  | 'PAID'
  | 'ACCEPTED'
  | 'IN_PREPARATION'
  | 'READY_FOR_PICKUP'
  | 'ON_THE_WAY'
  | 'DELIVERED'
  | 'CANCELLED';

// Ta sama lista co OrderHistoryService.HISTORY_STATUSES na BE. Nieoplacone zamowienia (i
// anulowane bez oplaty - porzucona platnosc) w historii sie nie pojawiaja.
export const HISTORY_STATUSES: OrderHistoryStatus[] = [
  'PAID',
  'ACCEPTED',
  'IN_PREPARATION',
  'READY_FOR_PICKUP',
  'ON_THE_WAY',
  'DELIVERED',
  'CANCELLED',
];

/** OrderStatusReason z BE. null - anulowanie sprzed zapisywania powodu (przed 2026-11-04). */
export type CancellationReason =
  | 'CANCELLED_BY_RESTAURANT'
  | 'NOT_ACCEPTED_IN_TIME'
  | 'NO_COURIER_FOUND'
  | 'COURIER_DID_NOT_PICK_UP';

export type CancellationKind = 'manual' | 'timeout' | 'unknown';

// Recznie = ktos kliknal "anuluj" w panelu; timeout = automat (brak akceptacji, brak kuriera,
// kurier nie odebral).
export const cancellationKind = (
  reason: CancellationReason | null | undefined,
): CancellationKind => {
  if (reason === 'CANCELLED_BY_RESTAURANT') return 'manual';
  if (reason) return 'timeout';
  return 'unknown';
};

export type OrderType = 'SELF_PICK_UP' | 'DELIVERY';
export const ORDER_TYPES: OrderType[] = ['SELF_PICK_UP', 'DELIVERY'];

export interface OrderHistoryListItem {
  id: number;
  orderNumber: string;
  createdAt: string;
  orderType: OrderType;
  paymentType: 'ONLINE' | 'CASH';
  status: OrderHistoryStatus;
  // Tylko dla CANCELLED.
  cancellationReason: CancellationReason | null;
  totalPrice: number;
  currency: string;
  customerName: string | null;
  locationId: number;
  locationName: string;
}

export interface OrderHistoryItemVariant {
  id: number;
  name?: string;
  variant?: string;
  category?: string;
  price?: string | number;
}

export interface OrderHistoryLine {
  id: number;
  itemTranslations: { language: string; translatedName: string }[];
  quantity: number;
  price: number;
  inventoryItemVariants: OrderHistoryItemVariant[];
  discountAmount: number;
  discountName: string | null;
  // Nazwa wpisana przez restauracje, w jej jezyku.
  name: string | null;
}

export interface OrderHistoryDetails {
  id: number;
  orderNumber: string;
  createdAt: string;
  orderType: OrderType;
  paymentType: 'ONLINE' | 'CASH';
  status: OrderHistoryStatus;
  // Tylko dla CANCELLED.
  cancellationReason: CancellationReason | null;
  locationId: number;
  locationName: string;
  customer: { name: string | null; phoneNumber: string | null; email: string | null } | null;
  deliveryAddress: string | null;
  woltTrackingUrl: string | null;
  serviceFee: number | null;
  totalPrice: number;
  orderDetails: {
    itemSubTotal: number;
    deliveryFee: number;
    couponCode: string | null;
    discountAmount: number;
    promoDiscountAmount: number;
    orderListItems: OrderHistoryLine[];
    currency: string;
    // Notatka klienta dla lokalu, np. "burger bez cebuli".
    note: string | null;
  };
  // Najstarszy pierwszy. Moze zawierac statusy spoza HISTORY_STATUSES (np. PENDING).
  statusHistory: { status: string; timestamp: string; reason: CancellationReason | null }[];
  payment: { status: string; amount: number; currency: string; refundedAt: string | null } | null;
}

/** Filtry listy trzymane przez react-admin: dni w strefie przegladarki, "YYYY-MM-DD". */
export interface OrderHistoryFilterValues {
  dateFrom?: string;
  dateTo?: string;
  status?: OrderHistoryStatus | '';
  orderType?: OrderType | '';
  q?: string;
  locationId?: number | '';
}

export const DEFAULT_RANGE_DAYS = 7;

const pad = (value: number) => String(value).padStart(2, '0');

export const toDateInputValue = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

// Ostatnie 7 dni lacznie z dzisiejszym.
export const defaultOrderHistoryFilters = (): OrderHistoryFilterValues => {
  const today = new Date();
  const from = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() - (DEFAULT_RANGE_DAYS - 1),
  );
  return { dateFrom: toDateInputValue(from), dateTo: toDateInputValue(today) };
};

const localMidnight = (day: string, plusDays = 0): Date => {
  const [year, month, date] = day.split('-').map(Number);
  return new Date(year, month - 1, date + plusDays);
};

/**
 * Filtry z panelu -> parametry GET /order/history. Dni sa liczone w strefie przegladarki:
 * od polnocy dnia "od" do polnocy dnia po "do" (koniec wylaczny). Puste pola nie ida w ogole.
 */
export const toOrderHistoryQuery = (filter: Record<string, any>): Record<string, any> => {
  const { dateFrom, dateTo, ...rest } = filter;
  const query: Record<string, any> = {};

  Object.entries(rest).forEach(([key, value]) => {
    if (value !== '' && value != null) query[key] = value;
  });

  let from = dateFrom || undefined;
  let to = dateTo || undefined;
  // Odwrocony zakres traktujemy jak poprawny, zamiast pokazywac blad.
  if (from && to && from > to) [from, to] = [to, from];
  if (from) query.createdFrom = localMidnight(from).toISOString();
  if (to) query.createdTo = localMidnight(to, 1).toISOString();

  return query;
};

export const formatDateTime = (iso: string | null | undefined, locale: string): string =>
  iso
    ? new Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: 'short' }).format(
        new Date(iso),
      )
    : '—';

export const formatAmount = (
  value: number | null | undefined,
  currency: string,
  locale: string,
): string =>
  new Intl.NumberFormat(locale, { style: 'currency', currency: currency || 'PLN' }).format(
    Number(value ?? 0),
  );

// Nazwa w jezyku panelu (language switcher), a gdy tlumaczenia brak - nazwa wpisana przez
// restauracje. Nigdy "pierwsze z listy": tlumaczenia przychodza bez stalej kolejnosci.
export const lineName = (line: OrderHistoryLine, backendLanguage: string): string =>
  line.itemTranslations?.find((t) => t.language === backendLanguage)?.translatedName ||
  line.name ||
  line.itemTranslations?.[0]?.translatedName ||
  '—';
